import * as React from "react";
import { useParams } from "react-router-dom";

import { useGetParams } from "@/hooks/useGetParams";
import { IProduct, IProductOverView } from "@/interfaces/tools.interface";
import { useGetSingleProductQuery } from "@/redux/api/toolsQuery";

type ProductDetailContextReturnType = {
  productId: string;
  productDetails: IProduct;
  productOverview: IProductOverView;
  isLoading: boolean;
};

type ProductDetailContextProviderProps = {
  children: React.ReactNode;
};

type ProductDetailsContextReturnType = {
  ProductDetailContextProvider: ({
    children,
  }: ProductDetailContextProviderProps) => JSX.Element;
  useProductDetailContext: () => ProductDetailContextReturnType;
};

const createProductDetailContext = (): ProductDetailsContextReturnType => {
  const ProductDetailContext = React.createContext<
    ProductDetailContextReturnType | undefined
  >(undefined);

  const ProductDetailContextProvider = ({
    children,
  }: ProductDetailContextProviderProps): JSX.Element => {
    const { id: productDetailId } = useParams();

    const filters = useGetParams(["search", "limit", "offset", "currentPage"]);
    const { data, isLoading } = useGetSingleProductQuery({
      filters,
      id: productDetailId!,
    });

    const productDetails = data?.data as IProduct;

    return (
      <ProductDetailContext.Provider
        value={{
          productId: productDetailId!,
          productDetails,
          isLoading,
          productOverview: productDetails?._count,
        }}
      >
        {children}
      </ProductDetailContext.Provider>
    );
  };

  const useProductDetailContext = (): ProductDetailContextReturnType => {
    const context = React.useContext(ProductDetailContext);
    if (context === undefined) {
      throw new Error(
        "useProductDetailContext must be used within a ProductDetailContextProvider",
      );
    }

    return context;
  };

  return { ProductDetailContextProvider, useProductDetailContext };
};

const { ProductDetailContextProvider, useProductDetailContext } =
  createProductDetailContext();

export { ProductDetailContextProvider, useProductDetailContext };
